import {
    Page,
    MainColumn,
    ContentPane,
    HeaderBar,
    TitleGroup,
    ViewToggle,
    ViewButton,
    StateMessage,
    GridScroll
} from './publishedMessageStyles';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';

import { MUTATION, QUERY } from '../../gql-operations';
import PublishedPageLayout from './PublishedPageLayout';
import FolderLinkActions from '../../components/Sharing/FolderLinkActions';
import PublishedFolderComposer from '../../components/Sharing/PublishedFolderComposer';
import ReadOnlyMessageGrid from '../../components/ReadOnlyContent/ReadOnlyMessageGrid';
import ReadOnlyMessageCard from '../../components/ReadOnlyContent/ReadOnlyMessageCard';
import ReadOnlyMessageChat from '../../components/ReadOnlyContent/ReadOnlyMessageChat';
import ReadOnlyMessageSidePreview from '../../components/ReadOnlyContent/ReadOnlyMessageSidePreview';
import { PRIMARY_COLOR, WHITE, PALE_WHITE_6 } from '../../appearance/Colors';
import { FiCheck, FiFolder, FiGrid, FiMessageCircle, FiX } from 'react-icons/fi';
import { IoMdSend } from 'react-icons/io';
import customAnalytics from '../../util/custom-analytics';
import {
    savePendingPublishedFolderCopyToken,
    savePublishedFolderAttribution
} from '../../util/pendingPublishedFolderCopy';

const VIEW_OPTIONS = {
    CHAT: 'chat',
    GRID: 'grid'
};
const LOAD_MORE_THRESHOLD_PX = 320;

const PublishedFolderPage = props => {
    const token = props.match.params.token;
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [ctaMessage, setCtaMessage] = useState('');
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalIntent, setAuthModalIntent] = useState(null);
    const [copyStatus, setCopyStatus] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const trackedViewTokenRef = useRef(null);
    const gridScrollRef = useRef(null);
    const viewParam = new URLSearchParams(props.location.search).get('view');
    const activeView = viewParam === VIEW_OPTIONS.GRID ? VIEW_OPTIONS.GRID : VIEW_OPTIONS.CHAT;

    const { data, loading, error, fetchMore, refetch } = useQuery(QUERY.GET_PUBLISHED_FOLDER, {
        variables: { data: { token } },
        skip: !token,
        fetchPolicy: 'network-only'
    });
    const [copyPublishedFolder, { loading: copyLoading }] = useMutation(MUTATION.COPY_PUBLISHED_FOLDER);

    const folder = data?.getPublishedFolder?.folder;

    useEffect(() => {
        if (folder?.title) {
            document.title = `${folder.title} — Luckynote`;
        }
    }, [folder]);

    useEffect(() => {
        if (!folder?.id || !token || trackedViewTokenRef.current === token) {
            return;
        }

        trackedViewTokenRef.current = token;
        savePublishedFolderAttribution(token, 'view');
        customAnalytics.published_folder_viewed({
            token,
            folderId: folder.id,
            folderTitle: folder.title,
            allowCopy: folder.allowCopy,
            discoverable: folder.discoverable,
            messageCount: data?.getPublishedFolder?.messages?.messages?.length || 0
        });
    }, [data?.getPublishedFolder?.messages?.messages?.length, folder, token]);

    if (loading) {
        return (
            <PublishedPageLayout fillViewport>
                <Page>
                    <StateMessage>Loading...</StateMessage>
                </Page>
            </PublishedPageLayout>
        );
    }

    if (error || !folder) {
        return (
            <PublishedPageLayout fillViewport>
                <Page>
                    <StateMessage>This link is no longer available.</StateMessage>
                </Page>
            </PublishedPageLayout>
        );
    }

    const messages = data?.getPublishedFolder?.messages?.messages || [];
    const nextCursor = data?.getPublishedFolder?.messages?.cursor;

    const handleSaveToLuckynote = async () => {
        setCopyStatus(null);

        if (!localStorage.getItem('token')) {
            savePendingPublishedFolderCopyToken(token);
            savePublishedFolderAttribution(token, 'copy');
            customAnalytics.published_folder_action({
                token,
                folderId: folder.id,
                folderTitle: folder.title,
                action: 'auth_prompt_opened',
                intent: 'copy',
                isLoggedIn: false
            });
            setAuthModalIntent('copy');
            setShowAuthModal(true);
            return;
        }

        try {
            customAnalytics.published_folder_action({
                token,
                folderId: folder.id,
                folderTitle: folder.title,
                action: 'copy_started',
                intent: 'copy',
                isLoggedIn: true
            });
            const result = await copyPublishedFolder({
                variables: {
                    data: { token }
                }
            });
            const copiedFolder = result?.data?.copyPublishedFolder?.folder;
            if (copiedFolder?.id) {
                customAnalytics.published_folder_action({
                    token,
                    folderId: folder.id,
                    folderTitle: folder.title,
                    action: 'copy_completed',
                    intent: 'copy',
                    isLoggedIn: true
                });
                setCopyStatus({
                    type: 'success',
                    folder: copiedFolder,
                    message: `Saved "${copiedFolder.title || folder.title}" to your Luckynote.`
                });
            }
        } catch (copyError) {
            customAnalytics.published_folder_action({
                token,
                folderId: folder.id,
                folderTitle: folder.title,
                action: 'copy_failed',
                intent: 'copy',
                isLoggedIn: true
            });
            setCopyStatus({
                type: 'error',
                message: copyError?.message || 'Unable to save this folder right now.'
            });
        }
    };

    const handleLoadMore = async () => {
        if (!nextCursor || loadingMore) {
            return;
        }

        setLoadingMore(true);
        try {
            await fetchMore({
                variables: { data: { token, messageCursor: nextCursor } },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const nextPage = fetchMoreResult?.getPublishedFolder;
                    if (!nextPage) {
                        return previousResult;
                    }

                    const previousMessages = previousResult?.getPublishedFolder?.messages?.messages || [];
                    const nextMessages = nextPage?.messages?.messages || [];

                    return {
                        ...previousResult,
                        getPublishedFolder: {
                            ...previousResult.getPublishedFolder,
                            messages: {
                                ...previousResult.getPublishedFolder.messages,
                                messages: [...previousMessages, ...nextMessages],
                                cursor: nextPage.messages.cursor
                            }
                        }
                    };
                }
            });
        } finally {
            setLoadingMore(false);
        }
    };

    const handleGridScroll = () => {
        const node = gridScrollRef.current;
        if (!node || !nextCursor || loadingMore) {
            return;
        }

        const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
        if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
            handleLoadMore();
        }
    };

    const setActiveView = nextView => {
        const searchParams = new URLSearchParams(props.location.search);

        if (nextView === VIEW_OPTIONS.CHAT) {
            searchParams.delete('view');
        } else {
            searchParams.set('view', nextView);
        }

        const nextSearch = searchParams.toString();
        props.history.replace({
            pathname: props.location.pathname,
            search: nextSearch ? `?${nextSearch}` : ''
        });
    };

    const headerContent = (
        <HeaderBar>
            <TitleGroup>
                <FiFolder size={18} />
                <h1>{folder.title}</h1>
            </TitleGroup>
            <ViewToggle aria-label="Published view">
                <ViewButton
                    type="button"
                    onClick={() => setActiveView(VIEW_OPTIONS.CHAT)}
                    $active={activeView === VIEW_OPTIONS.CHAT}
                    title="Chat view"
                    aria-label="Chat view"
                >
                    <FiMessageCircle size={15} />
                </ViewButton>
                <ViewButton
                    type="button"
                    onClick={() => setActiveView(VIEW_OPTIONS.GRID)}
                    $active={activeView === VIEW_OPTIONS.GRID}
                    title="Grid view"
                    aria-label="Grid view"
                >
                    <FiGrid size={15} />
                </ViewButton>
            </ViewToggle>
        </HeaderBar>
    );
    const copyAction = folder.allowCopy ? (
        <SaveButton type="button" onClick={handleSaveToLuckynote} disabled={copyLoading}>
            <FiCheck size={15} />
            {copyLoading ? 'Saving...' : 'Save to my Luckynote'}
        </SaveButton>
    ) : null;

    const headerAction = (
        <>
            {copyAction}
            <FolderLinkActions
                token={token}
                linkRole={folder.linkRole}
                onOpenInApp={
                    localStorage.getItem('token') ? () => props.history.push(`/published/${token}`) : undefined
                }
            />
        </>
    );

    return (
        <PublishedPageLayout fillViewport headerContent={headerContent} headerAction={headerAction}>
            <Page>
                <MainColumn>
                    {copyStatus ? (
                        <CopyStatus $type={copyStatus.type}>
                            <span>{copyStatus.message}</span>
                            {copyStatus.type === 'success' && copyStatus.folder?.id ? (
                                <CopyStatusButton
                                    type="button"
                                    onClick={() => props.history.push(`/f/${copyStatus.folder.id}`)}
                                >
                                    Open folder
                                </CopyStatusButton>
                            ) : null}
                        </CopyStatus>
                    ) : null}

                    <ContentPane $isGrid={activeView === VIEW_OPTIONS.GRID}>
                        {messages.length === 0 ? (
                            <StateMessage>This folder is empty.</StateMessage>
                        ) : activeView === VIEW_OPTIONS.CHAT ? (
                            <ReadOnlyMessageChat
                                bottomUp
                                messages={messages}
                                onSelectMessage={setSelectedMessage}
                                onLoadMore={handleLoadMore}
                                hasMore={Boolean(nextCursor)}
                                loadingMore={loadingMore}
                            />
                        ) : (
                            <GridScroll ref={gridScrollRef} onScroll={handleGridScroll}>
                                <ReadOnlyMessageGrid>
                                    {messages.map(message => (
                                        <ReadOnlyMessageCard
                                            key={message.id}
                                            message={message}
                                            onClick={() => setSelectedMessage(message)}
                                        />
                                    ))}
                                </ReadOnlyMessageGrid>
                                {loadingMore ? <LoadingMoreText>Loading...</LoadingMoreText> : null}
                            </GridScroll>
                        )}
                    </ContentPane>

                    <PublishedFolderComposer
                        token={token}
                        linkRole={folder.linkRole}
                        allowAnonymousContributions={folder.allowAnonymousContributions}
                        onAdded={() => refetch()}
                    />
                    {/* Legacy signup-only composer, replaced by link-aware contributions.
                    <PublicComposer
                        onSubmit={event => {
                            event.preventDefault();
                            if (!ctaMessage.trim()) {
                                return;
                            }
                            savePublishedFolderAttribution(token, 'composer');
                            customAnalytics.published_folder_action({
                                token,
                                folderId: folder.id,
                                folderTitle: folder.title,
                                action: 'auth_prompt_opened',
                                intent: 'composer',
                                isLoggedIn: Boolean(localStorage.getItem('token'))
                            });
                            setAuthModalIntent('composer');
                            setShowAuthModal(true);
                        }}
                    >
                        <ComposerInput
                            value={ctaMessage}
                            onChange={event => setCtaMessage(event.target.value)}
                            placeholder="Message this folder..."
                            aria-label="Message this folder"
                        />
                        {ctaMessage.trim() ? (
                            <ComposerButton type="submit" aria-label="Create an account to send">
                                <IoMdSend size={24} color={WHITE} />
                            </ComposerButton>
                        ) : null}
                    </PublicComposer>
                    */}
                </MainColumn>

                <ReadOnlyMessageSidePreview message={selectedMessage} onClose={() => setSelectedMessage(null)} />
            </Page>

            {showAuthModal ? (
                <AuthModalOverlay
                    onMouseDown={event => {
                        if (event.target === event.currentTarget) {
                            setShowAuthModal(false);
                        }
                    }}
                >
                    <AuthModal role="dialog" aria-modal="true" aria-labelledby="published-auth-modal-title">
                        <AuthModalClose type="button" onClick={() => setShowAuthModal(false)} aria-label="Close">
                            <FiX size={18} />
                        </AuthModalClose>
                        <AuthModalTitle id="published-auth-modal-title">Continue in Luckynote</AuthModalTitle>
                        <AuthModalText>
                            {authModalIntent === 'copy'
                                ? 'Create an account or log in to save this folder to your Luckynote.'
                                : 'Create an account or log in to add your message to this shared folder.'}
                        </AuthModalText>
                        <AuthModalActions>
                            <AuthPrimaryButton
                                type="button"
                                onClick={() => {
                                    savePublishedFolderAttribution(token, authModalIntent || 'signup');
                                    customAnalytics.published_folder_action({
                                        token,
                                        folderId: folder.id,
                                        folderTitle: folder.title,
                                        action: 'create_account_clicked',
                                        intent: authModalIntent,
                                        isLoggedIn: false
                                    });
                                    props.history.push('/register');
                                }}
                            >
                                Create account
                            </AuthPrimaryButton>
                            <AuthSecondaryButton
                                type="button"
                                onClick={() => {
                                    customAnalytics.published_folder_action({
                                        token,
                                        folderId: folder.id,
                                        folderTitle: folder.title,
                                        action: 'login_clicked',
                                        intent: authModalIntent,
                                        isLoggedIn: false
                                    });
                                    props.history.push('/login');
                                }}
                            >
                                Log in
                            </AuthSecondaryButton>
                        </AuthModalActions>
                    </AuthModal>
                </AuthModalOverlay>
            ) : null}
        </PublishedPageLayout>
    );
};

const SaveButton = styled.button`
    height: 38px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 9px;
    padding: 0 12px;
    color: ${WHITE};
    background: rgba(255, 255, 255, 0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;

    @media (max-width: 620px) {
        height: 34px;
        padding: 0 10px;
        font-size: 12px;
    }

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.14);
    }

    &:disabled {
        cursor: default;
        opacity: 0.68;
    }
`;

const CopyStatus = styled.div`
    flex: 0 0 auto;
    width: min(100%, 760px);
    margin: 0 auto 10px;
    padding: 10px 12px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: ${WHITE};
    background: ${({ $type }) => ($type === 'error' ? 'rgba(214, 62, 62, 0.18)' : 'rgba(41, 171, 135, 0.18)')};
    box-shadow: inset 0 0 0 1px
        ${({ $type }) => ($type === 'error' ? 'rgba(214, 62, 62, 0.3)' : 'rgba(41, 171, 135, 0.3)')};
    font-size: 13px;
    line-height: 1.35;

    @media (max-width: 620px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const CopyStatusButton = styled.button`
    flex: 0 0 auto;
    height: 32px;
    border: 0;
    border-radius: 8px;
    padding: 0 10px;
    color: ${PRIMARY_COLOR};
    background: ${WHITE};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
    }
`;

const LoadingMoreText = styled.div`
    color: ${PALE_WHITE_6};
    font-size: 12px;
    text-align: center;
    padding: 12px 0;
`;

/* Box shape matches the real compose bar (InputWrapper in
   client/src/components/MessageInput.js: 12px radius, inset border via
   box-shadow, blurred translucent fill) instead of an invented pill shape. */
const PublicComposer = styled.form`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
    width: min(100%, 760px);
    margin: 10px auto 0;
    padding: 4px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.16), inset 0 0 0 1px rgba(255, 255, 255, 0.11);
    backdrop-filter: saturate(160%) blur(10px);
    -webkit-backdrop-filter: saturate(160%) blur(10px);
`;

const ComposerInput = styled.input`
    flex: 1 1 auto;
    min-width: 0;
    min-height: 38px;
    border: 0;
    border-radius: 0;
    padding: 8px 10px;
    color: ${WHITE};
    background: transparent;
    font-size: 14px;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.42);
    }
`;

/* Plain icon button (matches IconWrapper in MessageInput.js) — no circular
   background, only shown once there is text, same as the real composer's
   send icon only appearing once getMeaningfulTextLength(...) > 0. */
const ComposerButton = styled.button`
    padding: 8px;
    border: 0;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
    border-radius: 8px;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const AuthModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 2400;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(0, 0, 0, 0.46);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
`;

const AuthModal = styled.div`
    position: relative;
    width: min(420px, 100%);
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 14px;
    padding: 24px;
    background: #222729;
    color: ${WHITE};
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
`;

const AuthModalClose = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    padding: 0;
    color: rgba(255, 255, 255, 0.68);
    background: rgba(255, 255, 255, 0.06);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        color: ${WHITE};
        background: rgba(255, 255, 255, 0.12);
    }
`;

const AuthModalTitle = styled.h2`
    margin: 0 36px 8px 0;
    font-size: 20px;
    line-height: 1.25;
    color: ${WHITE};
`;

const AuthModalText = styled.p`
    margin: 0;
    color: rgba(255, 255, 255, 0.66);
    font-size: 14px;
    line-height: 1.5;
`;

const AuthModalActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 22px;
`;

const AuthPrimaryButton = styled.button`
    flex: 1;
    height: 40px;
    border: 0;
    border-radius: 9px;
    color: ${PRIMARY_COLOR};
    background: ${WHITE};
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
    }
`;

const AuthSecondaryButton = styled.button`
    flex: 1;
    height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 9px;
    color: ${WHITE};
    background: rgba(255, 255, 255, 0.06);
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.11);
    }
`;

export default PublishedFolderPage;
